import React, {useEffect, useState} from "react";
import localforage from "localforage";
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import DashboardFooter from "../layouts/dashboard_footer";
import { buildApiUrl, useBaseUrl } from "../../utils/utils";
import Swal from "sweetalert2";

interface StaffProfile {
    id: number;
    fname: string;
    mname: string;
    lname: string;
    email: string;
    phone: string;
    avatar: string | null;
    active: boolean;
    two_fa_method: string;
    allow_public_search: boolean;
    title: { id: number; name: string } | null;
    town: { id: number; name: string } | null;
    clinic: { id: number; name: string } | null;
    institution: { id: number; name: string } | null;
}

interface TownOption {
    id: number;
    name: string;
    county: string;
}

const Profile: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicName, setClinicName] = useState<any>("Facility");
    const [staffId, setStaffId] = useState<number | null>(null);

    const [profile, setProfile] = useState<StaffProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ fname: '', mname: '', lname: '', phone: '', town_id: '' });
    const [towns, setTowns] = useState<TownOption[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        document.title = "Uzima - My Profile";
        localforage.getItem("clinic_name").then((clini) => setClinicName(clini));
        localforage.getItem("staff_id").then((sid: any) => setStaffId(sid ? Number(sid) : null));
    }, []);

    useEffect(() => {
        if (!staffId) return;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const res = await fetch(buildApiUrl(baseUrl, `facilities/staff/me?staff_id=${staffId}`));
                const data = await res.json();
                if (res.ok && data.status === 'success') {
                    setProfile(data.data);
                    setForm({
                        fname: data.data.fname, mname: data.data.mname, lname: data.data.lname,
                        phone: data.data.phone, town_id: data.data.town?.id ? String(data.data.town.id) : '',
                    });
                } else {
                    setError(data.message || 'Could not load your profile.');
                }
            } catch {
                setError('Network error while loading your profile.');
            } finally {
                setLoading(false);
            }
        })();
    }, [staffId, baseUrl]);

    useEffect(() => {
        if (!editing || towns.length > 0) return;
        (async () => {
            try {
                const res = await fetch(buildApiUrl(baseUrl, `practitioners/towns/`));
                const data = await res.json();
                setTowns(data.data || []);
            } catch { /* town list is optional - fall back to a plain text field */ }
        })();
    }, [editing, baseUrl, towns.length]);

    const startEditing = () => {
        if (!profile) return;
        setForm({
            fname: profile.fname, mname: profile.mname, lname: profile.lname,
            phone: profile.phone, town_id: profile.town?.id ? String(profile.town.id) : '',
        });
        setEditing(true);
    };

    const saveProfile = async () => {
        if (!staffId) return;
        if (!form.fname.trim() || !form.lname.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'First and last name are required.' });
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/staff/me`), {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    staff_id: staffId, fname: form.fname, mname: form.mname, lname: form.lname,
                    phone: form.phone, town_id: form.town_id || null,
                }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                setProfile(data.data);
                setEditing(false);
                Swal.fire({ icon: 'success', title: 'Profile Updated', timer: 1500, showConfirmButton: false });
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to update profile.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while saving your profile.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <DashboardHeader></DashboardHeader>
            <SideMenu></SideMenu>

            <div className={"vertical-overlay"}></div>

            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className={"container-fluid"}>

                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">{clinicName} - My Profile &nbsp;</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/home">Dashboards</a></li>
                                            <li className="breadcrumb-item active">Profile</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger">{error}</div>
                        ) : !staffId ? (
                            <div className="alert alert-warning">Could not identify your staff account. Please log in again.</div>
                        ) : profile && (
                            <div className="row">
                                <div className="col-lg-4">
                                    <div className="card">
                                        <div className="card-body text-center">
                                            <img
                                                src={profile.avatar || '/assets/images/users/avatar-1.jpg'}
                                                alt="avatar"
                                                className="rounded-circle avatar-lg img-thumbnail mb-3"
                                            />
                                            <h5 className="mb-1">{profile.fname} {profile.mname} {profile.lname}</h5>
                                            <p className="text-muted mb-2">{profile.title?.name || 'Staff'}</p>
                                            <span className={`badge ${profile.active ? 'bg-success' : 'bg-secondary'}`}>
                                                {profile.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card">
                                        <div className="card-body">
                                            <h6 className="text-uppercase text-muted mb-3">Facility</h6>
                                            <p className="mb-1"><i className="mdi mdi-hospital-building me-2"></i>{profile.institution?.name || '—'}</p>
                                            <p className="mb-0"><i className="mdi mdi-office-building-marker me-2"></i>{profile.clinic?.name || '—'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-8">
                                    <div className="card">
                                        <div className="card-header d-flex align-items-center">
                                            <h5 className="card-title mb-0 flex-grow-1">Personal Information</h5>
                                            {!editing ? (
                                                <button className="btn btn-sm btn-primary" onClick={startEditing}>
                                                    <i className="ri-pencil-fill align-bottom me-1"></i> Edit
                                                </button>
                                            ) : (
                                                <div className="d-flex gap-2">
                                                    <button className="btn btn-sm btn-light" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                                                    <button className="btn btn-sm btn-success" onClick={saveProfile} disabled={saving}>
                                                        {saving ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="card-body">
                                            {!editing ? (
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <p className="text-muted mb-1 small">First Name</p>
                                                        <p className="fw-medium">{profile.fname}</p>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="text-muted mb-1 small">Middle Name</p>
                                                        <p className="fw-medium">{profile.mname || '—'}</p>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="text-muted mb-1 small">Last Name</p>
                                                        <p className="fw-medium">{profile.lname}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p className="text-muted mb-1 small">Email</p>
                                                        <p className="fw-medium">{profile.email}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p className="text-muted mb-1 small">Phone</p>
                                                        <p className="fw-medium">{profile.phone || '—'}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p className="text-muted mb-1 small">Town</p>
                                                        <p className="fw-medium">{profile.town?.name || '—'}</p>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p className="text-muted mb-1 small">Two-Factor Method</p>
                                                        <p className="fw-medium text-capitalize">{profile.two_fa_method}</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <label className="form-label">First Name</label>
                                                        <input type="text" className="form-control" value={form.fname}
                                                               onChange={(e) => setForm(f => ({ ...f, fname: e.target.value }))}/>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label">Middle Name</label>
                                                        <input type="text" className="form-control" value={form.mname}
                                                               onChange={(e) => setForm(f => ({ ...f, mname: e.target.value }))}/>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label className="form-label">Last Name</label>
                                                        <input type="text" className="form-control" value={form.lname}
                                                               onChange={(e) => setForm(f => ({ ...f, lname: e.target.value }))}/>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Email</label>
                                                        <input type="text" className="form-control" value={profile.email} disabled/>
                                                        <div className="form-text">Contact support to change your login email.</div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Phone</label>
                                                        <input type="text" className="form-control" value={form.phone}
                                                               onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}/>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Town</label>
                                                        <select className="form-select" value={form.town_id}
                                                                onChange={(e) => setForm(f => ({ ...f, town_id: e.target.value }))}>
                                                            <option value="">Select a town</option>
                                                            {towns.map(t => (
                                                                <option key={t.id} value={t.id}>{t.name} ({t.county})</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <DashboardFooter></DashboardFooter>
            </div>
        </>
    )
}

export default Profile
